import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, XMarkIcon, TableCellsIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import type { Plan } from '@/types';
import { BrandMark } from '@/components/ui/brand-loader';

interface PricingSectionProps {
  plans: Plan[];
  isLoading: boolean;
  showComparison?: boolean;
}

const formatPlatformName = (platform: string) => {
  if (platform.toLowerCase() === 'tiktok') return 'TikTok';
  if (platform.toLowerCase() === 'youtube') return 'YouTube';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
};

const formatPlatformList = (platforms: string[]) => {
  if (!platforms.length) return 'No connected platforms';
  if (platforms.length === 1) return formatPlatformName(platforms[0] ?? '');
  const names = platforms.map(formatPlatformName);
  const lastName = names[names.length - 1] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${lastName}`;
};

const formatBoardLimit = (maxBoards: number) => {
  if (maxBoards === -1) return 'Unlimited board workspaces';
  return `${maxBoards} board workspace${maxBoards === 1 ? '' : 's'}`;
};

const getPlanMonthlyAiCredits = (plan: Plan) => {
  if (typeof plan.monthly_ai_credits === 'number') {
    return plan.monthly_ai_credits;
  }

  const fallbackByTier: Record<Plan['tier'], number> = {
    free: 0,
    starter: 100,
    pro: 500,
    agency: 2000,
  };

  return fallbackByTier[plan.tier] ?? 0;
};

const formatCredits = (credits: number) => credits.toLocaleString();

// ── Billing Toggle ──────────────────────────────────────────────
function BillingToggle({
  isAnnual,
  onToggle,
}: {
  isAnnual: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center mb-12">
      <div className="relative flex items-center justify-center gap-3">
        <span
          className={`text-sm font-semibold transition-colors ${
            !isAnnual ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          {t('landing.pricingSection.monthly')}
        </span>
        <button
          onClick={onToggle}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
            isAnnual
              ? 'bg-gradient-to-r from-primary-500 to-purple-500'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
          aria-label={t('landing.pricingSection.toggleBilling')}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
              isAnnual ? 'translate-x-8' : 'translate-x-1'
            }`}
          />
        </button>
        <span
          className={`text-sm font-semibold transition-colors ${
            isAnnual ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          {t('landing.pricingSection.annual')}
        </span>
        <div className="mt-2 flex items-center whitespace-nowrap sm:absolute sm:left-full sm:ml-3 sm:mt-0">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold border transition-colors ${
              isAnnual
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 animate-pulse'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
            }`}
          >
            2 months free
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Feature Check / Cross ───────────────────────────────────────
function FeatureValue({
  value,
  isPro = false,
}: {
  value: boolean | string | number;
  isPro?: boolean;
}) {
  if (typeof value === 'boolean') {
    return value ? (
      <CheckCircleIcon className={`h-5 w-5 mx-auto ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
    ) : (
      <XMarkIcon className="h-5 w-5 mx-auto text-gray-300 dark:text-gray-600" />
    );
  }
  return (
    <span className={`text-sm font-medium ${isPro ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
      {value}
    </span>
  );
}

// ── Plan Card ───────────────────────────────────────────────────
function PlanCard({
  plan,
  isAnnual,
  isPopular,
}: {
  plan: Plan;
  isAnnual: boolean;
  isPopular: boolean;
}) {
  const { t } = useTranslation();
  const isPro = plan.tier === 'pro';
  const isAgency = plan.tier === 'agency';
  const isStarter = plan.tier === 'starter';
  const monthlyPrice = parseFloat(plan.price);
  const annualTotal = plan.annual_price ? parseFloat(plan.annual_price) : monthlyPrice * 10;
  const annualMonthly = annualTotal / 12;
  const displayPrice = isAnnual ? annualMonthly : monthlyPrice;
  const savingsPercent = Math.round(
    ((monthlyPrice * 12 - annualTotal) / (monthlyPrice * 12)) * 100
  );
  const formatDaysLimit = (days?: number | null) => {
    if (!days || days < 0) return 'Unlimited';
    return `${days} day${days === 1 ? '' : 's'}`;
  };

  return (
    <div className="relative flex flex-col h-full" data-testid="plan-card">
      {/* Smoke Glow for Pro */}
      {isPro && (
        <>
          <style>
            {`
              @keyframes floatY1 {
                0%, 100% { transform: translateY(-10px) scaleY(1.02); }
                50% { transform: translateY(10px) scaleY(0.98); }
              }
              @keyframes floatY2 {
                0%, 100% { transform: translateY(10px) scaleY(0.98); }
                50% { transform: translateY(-10px) scaleY(1.02); }
              }
            `}
          </style>
          <div className="absolute inset-y-[10px] inset-x-[20px] -z-10 pointer-events-none">
            {/* Layer 1: Floating light blue to pink */}
            <div 
              className="absolute inset-0 bg-gradient-to-tr from-sky-300 via-blue-400 to-pink-400 rounded-[40px] blur-[30px] opacity-60 dark:opacity-80 mix-blend-multiply dark:mix-blend-screen"
              style={{ animation: 'floatY1 2s ease-in-out infinite' }}
            />
            {/* Layer 2: Floating reverse pink to light blue */}
            <div 
              className="absolute inset-0 bg-gradient-to-bl from-pink-300 via-fuchsia-400 to-cyan-400 rounded-[40px] blur-[30px] opacity-60 dark:opacity-80 mix-blend-multiply dark:mix-blend-screen"
              style={{ animation: 'floatY2 3s ease-in-out infinite' }}
            />
          </div>
        </>
      )}

      {/* Smoke Glow for Agency */}
      {/* isAgency && (
        <>
          <style>
            {\`
              @keyframes floatY1Agency {
                0%, 100% { transform: translateY(-10px) scaleY(1.02); }
                50% { transform: translateY(10px) scaleY(0.98); }
              }
              @keyframes floatY2Agency {
                0%, 100% { transform: translateY(10px) scaleY(0.98); }
                50% { transform: translateY(-10px) scaleY(1.02); }
              }
            \`}
          </style>
          <div className="absolute inset-y-[10px] inset-x-[20px] -z-10 pointer-events-none">
            <div 
              className="absolute inset-0 bg-gradient-to-tr from-amber-400 via-orange-500 to-rose-500 rounded-[40px] blur-[30px] opacity-10 dark:opacity-20 mix-blend-multiply dark:mix-blend-screen"
              style={{ animation: 'floatY1Agency 2.2s ease-in-out infinite' }}
            />
            <div 
              className="absolute inset-0 bg-gradient-to-bl from-yellow-400 via-red-500 to-pink-600 rounded-[40px] blur-[30px] opacity-10 dark:opacity-20 mix-blend-multiply dark:mix-blend-screen"
              style={{ animation: 'floatY2Agency 2.8s ease-in-out infinite' }}
            />
          </div>
        </>
      ) */}

      <div
        className={`relative z-10 rounded-[2rem] p-8 flex flex-col h-full backdrop-blur-[40px] border shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
          isPro
            ? 'md:scale-105 bg-white/70 dark:bg-[#0B0E14]/30 border-primary-500/60 dark:border-primary-400/60 shadow-[0_20px_80px_rgba(168,85,247,0.2)] dark:shadow-[0_20px_80px_rgba(168,85,247,0.4)]'
            : isAgency
            ? 'bg-white/70 dark:bg-[#0B0E14]/30 border-orange-500/30 dark:border-orange-400/30 shadow-[0_20px_80px_rgba(249,115,22,0.1)] dark:shadow-[0_20px_80px_rgba(249,115,22,0.2)]'
            : isStarter
            ? 'bg-white/30 dark:bg-[#0B0E14]/30 border-blue-400/40 dark:border-blue-500/40 shadow-[0_8px_30px_rgb(59,130,246,0.08)]'
            : 'bg-white/30 dark:bg-[#0B0E14]/30 border-gray-200/50 dark:border-gray-700/60 shadow-[0_8px_30px_rgb(0,0,0,0.06)]'
        }`}
      >
        {isPopular && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 via-primary-500 to-purple-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg shadow-primary-500/30">
            {t('subscription.pricing.mostPopular')}
          </div>
        )}

        <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
          {plan.name}
        </h3>
        <p className="text-sm mb-6 text-gray-600 dark:text-gray-400">
          {plan.description}
        </p>

        {/* Price */}
        <div className="mb-8">
          <div className="flex items-baseline gap-1">
            <span className="text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              ${isAnnual ? annualMonthly.toFixed(2) : displayPrice.toFixed(0)}
            </span>
            <span className="text-base text-gray-500 dark:text-gray-400">
              /mo
            </span>
          </div>
          {isAnnual && (
            <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
              Billed ${annualTotal.toFixed(0)}/year · Save {savingsPercent}%
            </p>
          )}
          {!isAnnual && (
            <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
              {t('landing.pricingSection.billedMonthly')}
            </p>
          )}
        </div>

        {/* Key Features */}
        <ul className="space-y-3 mb-6 flex-1 text-[15px]">
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {formatBoardLimit(plan.max_boards)}
            </span>
          </li>
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {formatPlatformList(plan.allowed_platforms)} included
            </span>
          </li>
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {t('landing.pricingSection.oneAccountPerPlatform')}
            </span>
          </li>
          {plan.has_content_analytics && (
            <li className="flex items-start">
              <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
              <span className="text-gray-800 dark:text-gray-200 font-medium">
                {t('landing.pricingSection.fullAnalytics')}
              </span>
            </li>
          )}
          {plan.has_team_collaboration && (
            <li className="flex items-start">
              <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
              <span className="text-gray-800 dark:text-gray-200 font-medium">
                Team collaboration ({plan.max_team_members === -1 ? 'unlimited' : `up to ${plan.max_team_members}`} members)
              </span>
            </li>
          )}
          {/* AI Credits */}
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {formatCredits(getPlanMonthlyAiCredits(plan))} AI credits/month
            </span>
          </li>
          {/* Analytics History */}
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {plan.analytics_history_days === -1 ? 'Unlimited' : `${plan.analytics_history_days} days`} analytics history
            </span>
          </li>
          <li className="flex items-start">
            <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {formatDaysLimit(plan.sync_period_days)} analytics sync period
            </span>
          </li>
          {plan.support_level === 'priority' && (
            <li className="flex items-start">
              <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
              <span className="text-gray-800 dark:text-gray-200 font-medium">
                {t('landing.pricingSection.prioritySupport')}
              </span>
            </li>
          )}
          {plan.support_level === 'dedicated' && (
            <li className="flex items-start">
              <CheckCircleIcon className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${isPro ? 'text-primary-500' : 'text-green-500'}`} />
              <span className="text-gray-800 dark:text-gray-200 font-medium">
                {t('landing.pricingSection.dedicatedSupport')}
              </span>
            </li>
          )}
        </ul>

        {/* CTA */}
        <div className="mt-auto pt-6 border-t border-gray-200/50 dark:border-gray-700/50">
          <Link to={`/register?plan=${plan.slug}`} className="block w-full">
            <Button
              size="lg"
              className={`w-full text-base font-bold transition-all shadow-sm hover:shadow-md ${
                isPro
                  ? 'bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-500 hover:to-purple-500 text-white border-none shadow-primary-500/25'
                  : 'bg-white hover:bg-blue-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700'
              }`}
            >
              {t('landing.pricing.startFreeTrial')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Comparison Table ────────────────────────────────────────────
type FeatureRow = {
  label: string;
  values: (boolean | string | number)[];
};
type FeatureCategory = {
  name: string;
  color: string;
  rows: FeatureRow[];
};

function getComparisonData(plans: Plan[]): FeatureCategory[] {
  // plans should be sorted: starter, pro, agency
  const p = plans;
  return [
    {
      name: 'Content Planning',
      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      rows: [
        { label: 'Board workspaces', values: p.map((pl) => (pl.max_boards === -1 ? 'Unlimited' : `${pl.max_boards}`)) },
        { label: 'Platforms', values: p.map((pl) => formatPlatformList(pl.allowed_platforms)) },
        { label: 'Kanban board', values: p.map(() => true) },
        { label: 'Calendar view', values: p.map(() => true) },
        // { label: 'Google Calendar sync', values: p.map((pl) => pl.has_google_calendar) },
        { label: 'Direct publishing', values: p.map(() => true) },
      ],
    },
    {
      name: 'Analytics',
      color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      rows: [
        { label: 'Channel insights', values: p.map((pl) => pl.has_oauth_analytics) },
        { label: 'Content analytics', values: p.map((pl) => pl.has_content_analytics) },
        { label: 'Analytics history', values: p.map((pl) => `${pl.analytics_history_days} days`) },
      ],
    },
    {
      name: 'Collaboration',
      color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      rows: [
        { label: 'Team collaboration', values: p.map((pl) => pl.has_team_collaboration) },
        { label: 'Team members', values: p.map((pl) => (pl.max_team_members === -1 ? 'Unlimited' : `${pl.max_team_members}`)) },
        // { label: 'Workspaces', values: p.map((pl) => (pl.max_workspaces === -1 ? 'Unlimited' : `${pl.max_workspaces}`)) },
        // { label: 'White label', values: p.map((pl) => pl.has_white_label) },
      ],
    },
    {
      name: 'AI Features',
      color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      rows: [
        { label: 'AI content generation', values: p.map(() => true) },
        { label: 'AI analytics insights', values: p.map(() => true) },
        { label: 'AI credits (monthly)', values: p.map((pl) => formatCredits(getPlanMonthlyAiCredits(pl))) },
      ],
    },
    {
      name: 'Support',
      color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      rows: [
        { label: 'Support level', values: p.map((pl) => pl.support_level.charAt(0).toUpperCase() + pl.support_level.slice(1)) },
        // { label: 'API access', values: p.map((pl) => pl.has_api_access) },
      ],
    },
  ];
}

function ComparisonTable({ plans }: { plans: Plan[] }) {
  const { t } = useTranslation();
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const sorted = [...plans].sort((a, b) => {
    const order: Record<string, number> = { starter: 0, pro: 1, agency: 2 };
    return (order[a.tier] || 0) - (order[b.tier] || 0);
  });

  const categories = getComparisonData(sorted);
  const activePlan = sorted[activePlanIndex] ?? sorted[0];

  return (
    <div className="mt-20">
      <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-2">
        {t('landing.pricingSection.comparePlans')}
      </h3>
      <p className="text-center text-gray-500 dark:text-gray-400 mb-10 text-base">
        {t('landing.pricingSection.findPerfectPlan')}
      </p>

      <div className="md:hidden">
        <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="grid grid-cols-3 gap-1">
            {sorted.map((plan, index) => {
              const isActive = index === activePlanIndex;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setActivePlanIndex(index)}
                  className={`min-h-11 rounded-xl px-2 text-sm font-bold transition-all ${
                    isActive
                      ? plan.tier === 'pro'
                        ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white shadow-md shadow-primary-500/20'
                        : 'bg-gray-950 text-white shadow-md shadow-gray-900/15 dark:bg-white dark:text-gray-950'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white'
                  }`}
                  aria-pressed={isActive}
                >
                  {plan.name}
                </button>
              );
            })}
          </div>
        </div>

        {activePlan && (
          <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">{t('auth.selectedPlan')}</p>
                <h4 className="mt-1 text-2xl font-bold text-gray-950 dark:text-white">
                  {activePlan.name}
                </h4>
              </div>
              {activePlan.tier === 'pro' && (
                <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
                  {t('landing.pricingSection.popular')}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {categories.map((category) => (
            <section
              key={category.name}
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                <span
                  className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider ${category.color}`}
                >
                  {category.name}
                </span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {category.rows.map((row) => (
                  <div
                    key={`${category.name}-${row.label}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-4 py-3.5"
                  >
                    <span className="min-w-0 text-sm font-medium text-gray-600 dark:text-gray-300">
                      {row.label}
                    </span>
                    <div className="max-w-[11rem] text-right">
                      <FeatureValue
                        value={row.values[activePlanIndex] ?? ''}
                        isPro={activePlan?.tier === 'pro'}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full max-w-5xl mx-auto">
          {/* Header */}
          <thead>
            <tr>
              <th className="text-left py-4 px-4 w-[40%]" />
              {sorted.map((plan) => (
                <th key={plan.id} className="text-center py-4 px-4 w-[20%]">
                  <div
                    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                      plan.tier === 'pro'
                        ? 'bg-gradient-to-r from-primary-500 to-purple-500 text-white shadow-lg shadow-primary-500/25'
                        : plan.tier === 'agency'
                        ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {plan.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {categories.map((category, catIdx) => (
              <Fragment key={category.name}>
                {/* Category Header */}
                <tr>
                  <td colSpan={sorted.length + 1} className="pt-8 pb-3 px-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${category.color}`}
                    >
                      {category.name}
                    </span>
                  </td>
                </tr>
                {/* Feature Rows */}
                {category.rows.map((row, rowIdx) => (
                  <tr
                    key={`${catIdx}-${rowIdx}`}
                    className={`border-b border-gray-100 dark:border-gray-800 ${
                      rowIdx % 2 === 0
                        ? 'bg-gray-50/50 dark:bg-gray-800/30'
                        : ''
                    }`}
                  >
                    <td className="py-3.5 px-4 text-sm text-gray-700 dark:text-gray-300 font-medium">
                      {row.label}
                    </td>
                    {row.values.map((val, valIdx) => (
                      <td key={valIdx} className="py-3.5 px-4 text-center">
                        <FeatureValue value={val} isPro={sorted[valIdx]?.tier === 'pro'} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main PricingSection ─────────────────────────────────────────
export default function PricingSection({ plans, isLoading, showComparison = false }: PricingSectionProps) {
  const [isAnnual, setIsAnnual] = useState(false);
  const { t } = useTranslation();

  const sortedPlans = [...plans].sort((a, b) => {
    const order: Record<string, number> = { free: 0, starter: 1, pro: 2, agency: 3 };
    return (order[a.tier] || 0) - (order[b.tier] || 0);
  });

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.pricing.title')}
            <span className="text-primary-600 dark:text-primary-400">
              {' '}{t('landing.pricing.titleHighlight')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('landing.pricing.subtitle')}
          </p>
        </div>

        {/* Toggle */}
        <BillingToggle isAnnual={isAnnual} onToggle={() => setIsAnnual(!isAnnual)} />

        {/* Plan Cards */}
        {isLoading ? (
          <div className="text-center py-12">
            <BrandMark size={48} variant="loading" className="mx-auto block" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('landing.pricingSection.loadingPlans')}</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {sortedPlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isAnnual={isAnnual}
                  isPopular={plan.tier === 'pro'}
                />
              ))}
            </div>

            <p className="text-center text-gray-600 dark:text-gray-400 mt-12 mb-8">
              {t('landing.pricing.trialInfo')}
            </p>

            {/* Conditional Comparison Table or CTA */}
            {showComparison ? (
              <ComparisonTable plans={sortedPlans} />
            ) : (
              <div className="text-center mt-12">
                <Link to="/pricing">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-none rounded-lg gap-2 group shadow-md hover:shadow-lg transition-all"
                    leftIcon={<TableCellsIcon className="h-5 w-5" />}
                    rightIcon={<ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                  >
                    {t('landing.pricingSection.seeFullComparison')}
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
